import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useRealtimeChannel } from '@/hooks/useRealtimeChannel'
import { type ActivityRow, fetchActivityFeed } from '@/lib/metrics'
import type { AdminEvent } from '@/lib/ws'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Activity, ArrowDown, Pause, Play } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'

interface ActivityEvent extends AdminEvent {
  type: string
  kind?: string
  summary?: string
  target_model?: string
  target_id?: string
  created_at?: string
}

function timeAgo(iso: string): string {
  const date = new Date(iso)
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

type FeedRow =
  | { source: 'audit'; row: ActivityRow }
  | { source: 'live'; id: string; event: ActivityEvent }

export function ActivityFeed() {
  const [paused, setPaused] = useState(false)
  const [liveRows, setLiveRows] = useState<ActivityEvent[]>([])
  const liveBuffer = useRef<ActivityEvent[]>([])

  // Initial load — recent audit log entries
  const { data, isLoading } = useQuery({
    queryKey: ['admin-activity-feed'],
    queryFn: () => fetchActivityFeed(undefined, 50),
    staleTime: 30_000
  })

  // Real-time push
  useRealtimeChannel('admin.activity', (event) => {
    const e = event as ActivityEvent
    if (e.type !== 'activity.append') return
    if (paused) {
      liveBuffer.current.push(e)
    } else {
      setLiveRows((prev) => [e, ...prev].slice(0, 200))
    }
  })

  // Drain buffered events when unpaused
  useEffect(() => {
    if (!paused && liveBuffer.current.length > 0) {
      setLiveRows((prev) =>
        [...liveBuffer.current.reverse(), ...prev].slice(0, 200)
      )
      liveBuffer.current = []
    }
  }, [paused])

  const rows = useMemo<FeedRow[]>(() => {
    const live: FeedRow[] = liveRows.map((event, i) => ({
      source: 'live' as const,
      id: `live-${event.created_at ?? i}-${i}`,
      event
    }))
    const seed: FeedRow[] = (data?.results ?? []).map((row) => ({
      source: 'audit' as const,
      row
    }))
    return [...live, ...seed]
  }, [liveRows, data])

  const bufferedCount = liveBuffer.current.length

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Live activity</h3>
          {paused && bufferedCount > 0 && (
            <Badge variant="secondary" className="tabular-nums">
              +{bufferedCount} new
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setPaused((p) => !p)}
          className="h-7"
        >
          {paused ? (
            <Play className="size-3.5" />
          ) : (
            <Pause className="size-3.5" />
          )}
          <span className="ml-1.5 text-xs">{paused ? 'Resume' : 'Pause'}</span>
        </Button>
      </div>

      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-10 animate-pulse rounded-md bg-muted/40"
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <ArrowDown className="size-5 opacity-40" />
            Quiet right now. New events show up here automatically.
          </div>
        ) : (
          <Virtuoso<FeedRow>
            data={rows}
            itemContent={(_idx, item) => (
              <ActivityFeedRow
                key={item.source === 'live' ? item.id : item.row.id}
                item={item}
              />
            )}
          />
        )}
      </div>
    </Card>
  )
}

function ActivityFeedRow({ item }: { item: FeedRow }) {
  const isLive = item.source === 'live'
  const summary = isLive
    ? (item.event.summary ??
      `${item.event.kind ?? 'event'}: ${item.event.target_model ?? ''}`)
    : `${item.row.actor_username ?? item.row.actor_type} → ${item.row.action}`
  const detail = isLive
    ? item.event.target_model
    : item.row.target_model && `${item.row.target_model}#${item.row.target_id}`
  const when = isLive ? item.event.created_at : item.row.created_at

  return (
    <motion.div
      initial={
        isLive
          ? { opacity: 0, y: -6, backgroundColor: 'rgba(34,197,94,0.08)' }
          : { opacity: 1 }
      }
      animate={{ opacity: 1, y: 0, backgroundColor: 'rgba(0,0,0,0)' }}
      transition={{ duration: 0.4 }}
      className="flex items-start gap-2 border-b px-4 py-2 text-sm last:border-0"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{summary}</div>
        {detail && (
          <div className="truncate text-xs text-muted-foreground">{detail}</div>
        )}
      </div>
      <div className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {when ? timeAgo(when) : ''}
      </div>
    </motion.div>
  )
}
